// ---------------------------------------------------------------------------
// The browser side of OFFER WAVES AND RESERVES. V27.
//
// NO STATE ID, NO TONE AND NO BASIS IS SPELLED HERE OR IN THE SCREEN. A wave
// arrives as its state id AND its label AND its tone; a rate basis arrives from
// `/v1/reference/rate-bases`; a reserve's status arrives with its label and with
// `is_commitment`, which is the column that tells an offer from an agreement.
//
// AND NO TIME IS COMPUTED IN THE BROWSER. `opens_at` and `closes_at` both arrive
// from the server, and `closes_at` is derived in SQL from the tier's PLANNED
// opening. A browser that added `response_minutes` to `opens_at` itself would be
// a fourth clock in a feature whose whole verify line is about there being one —
// and it would be the one clock the user can change.
// ---------------------------------------------------------------------------

import { mutate, read } from "./console";

export type OfferWave = {
  wave_id: string;
  tier: number;
  /** The band of ranks in the V26 ranking this tier covers. */
  from_rank: number;
  to_rank: number;
  opens_at: string;
  /** Derived in SQL from opens_at. Never recomputed here. */
  closes_at: string;
  response_minutes: number;
  state: string;
  state_label: string;
  tone: string;
  /** S70 (0203). This state means the cascade is waiting on somebody to run it. */
  waits_on_a_run: boolean;
  offered_count: number;
  answered_count: number;
  accepted_count: number;
  /**
   * When the scheduler got round to it. An OBSERVATION — nothing decides
   * anything from this, and a wave whose opening has passed with this still null
   * is the `due` state, which means the scheduler has not run.
   */
  fired_at: string | null;
};

export type OfferReserve = {
  assignment_id: string;
  worker_id: string;
  worker_name: string;
  status: string;
  status_label: string;
  /** FALSE for an offer nobody has answered. Not a synonym for "holds a seat". */
  is_commitment: boolean;
  starts_at: string;
  ends_at: string;
};

export type StandbyRate = {
  rate_minor: number | null;
  basis: string | null;
  basis_label: string | null;
  currency: string;
};

export const offers = {
  waves: (base: string, requirementId: string) =>
    read(base, `/v1/requirements/${encodeURIComponent(requirementId)}/offer-waves`),

  plan: (
    base: string,
    requirementId: string,
    payload: { match_run_id: string; tier_size: number; response_minutes: number },
  ) => mutate("POST", base, `/v1/requirements/${encodeURIComponent(requirementId)}/offer-waves`, payload),

  // NO INSTANT IS SENT. The database reads its own clock; the replay parameter
  // exists for reproducing a past run and never reaches the network.
  fire: (base: string) => mutate("POST", base, "/v1/offer-waves/fire", {}),

  // AN ABSENT PAIR MEANS "NO STANDBY PAYMENT", not "leave it as it was". The
  // endpoint owns exactly two columns that are both-or-neither.
  setStandby: (
    base: string,
    requirementId: string,
    payload: { rate_minor?: number; basis?: string },
  ) => mutate("PATCH", base, `/v1/requirements/${encodeURIComponent(requirementId)}/standby-rate`, payload),
};
